import React from 'react'
import { cn } from '../../lib/utils'
import { Card, CardContent } from './card'

interface MaterialCardProps
{
    children: React.ReactNode
    className?: string
    onClick?: () => void
}

export const MaterialCard: React.FC<MaterialCardProps> = ( { children, className = '', onClick } ) =>
{
    return (
        <Card
            className={ cn(
                'group relative border-0',
                'premium-card',
                onClick && 'cursor-pointer active:scale-[0.99]',
                className,
            ) }
            onClick={ onClick }
        >
            <div className="relative z-10">{ children }</div>
        </Card>
    )
}

export const MetricCard: React.FC<{
    title: React.ReactNode
    value: React.ReactNode
    change?: number
    changeP?: number
    icon?: React.ReactNode
    subtext?: React.ReactNode
    className?: string
    compact?: boolean
}> = React.memo( ( { title, value, change, changeP, icon, subtext, className = '', compact = false } ) =>
{
    const isPositive = ( change || 0 ) >= 0

    return (
        <Card
            className={ cn(
                'relative overflow-hidden group select-none border-0',
                'premium-card',
                compact ? 'p-3.5' : 'p-5',
                'bg-white dark:bg-slate-900',
                'ring-1 ring-black/5 dark:ring-white/10',
                'shadow-sm',
                'hover:shadow-md hover:ring-black/10 dark:hover:ring-white/20',
                'transition-all duration-300 ease-out',
                'hover:-translate-y-0.5',
                // Performance Optimization: Hint browser for transform changes on hover
                'will-change-transform',
                className,
            ) }
        >
            {/* Content Container */ }
            <div className={`relative z-10 flex flex-col w-full ${compact ? 'gap-1' : 'gap-2'}`}>
                {/* Header: Title & Icon */ }
                <div className="flex justify-between items-start gap-2">
                    <div className="flex flex-col">
                        <span
                            className={ `
              ${compact ? 'text-[10px]' : 'text-xs'} 
              font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest leading-tight
              group-hover:text-slate-800 dark:group-hover:text-slate-300 transition-colors duration-300
            `}
                        >
                            { title }
                        </span>
                    </div>
                    { icon && (
                        <div
                            className={ `
              flex items-center justify-center
              ${compact ? 'w-8 h-8' : 'w-10 h-10'}
              rounded-2xl
              bg-slate-50/50 dark:bg-slate-800/50 
              group-hover:bg-slate-100 dark:group-hover:bg-slate-700
              group-hover:scale-105
              transition-all duration-300 ease-out
            `}
                        >
                            { React.cloneElement(
                                icon as React.ReactElement,
                                {
                                    size: compact ? 14 : 18,
                                    strokeWidth: 2.5,
                                } as any,
                            ) }
                        </div>
                    ) }
                </div>

                {/* Main Value Area */}
                <div className="flex flex-col gap-0.5">
                    {(change !== undefined || changeP !== undefined) ? (
                        /* Row layout: value left, change right — for cards with change data */
                        <div className="flex items-end justify-between gap-2">
                            <div
                                className={`
                font-black text-slate-800 dark:text-slate-100 tracking-tighter leading-none premium-cell-mono
                ${compact ? 'text-[22px]' : 'text-3xl'}
                transition-colors duration-300
              `}
                            >
                                { value }
                            </div>
                            <div className="flex items-center gap-1.5 whitespace-nowrap shrink-0">
                                {change !== undefined && (
                                    <div
                                        className={`
                      flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold border backdrop-blur-sm
                      ${isPositive
                                            ? 'bg-emerald-50/50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-100/30 dark:border-emerald-800/30'
                                            : 'bg-rose-50/50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-100/30 dark:border-rose-800/30'
                                        }
                    `}
                                    >
                                        <span>{ isPositive ? '▲' : '▼' }</span>
                                        <span className="premium-cell-mono">
                                            { Math.abs(change).toLocaleString('en-IN', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }) }
                                        </span>
                                    </div>
                                )}
                                {changeP !== undefined && (
                                    <span
                                        className={`
                      text-xs font-bold premium-cell-mono
                      ${isPositive ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}
                    `}
                                    >
                                        { Math.abs(changeP).toFixed(2) }%
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Simple stacked layout — for cards without change data */
                        <div
                            className={`
                font-black text-slate-800 dark:text-slate-100 tracking-tighter leading-none premium-cell-mono
                ${compact ? 'text-[22px]' : 'text-3xl'}
                transition-colors duration-300
              `}
                        >
                            { value }
                        </div>
                    )}

                    {/* Subtext Area */ }
                    { subtext && (
                        <div
                            className={ `
               ${compact ? 'pt-1.5' : 'pt-2'}
               flex items-center border-t border-white/20 dark:border-slate-800 group-hover:border-white/40 dark:group-hover:border-slate-700 transition-colors duration-300
             `}
                        >
                            <div
                                className={ `
                  text-[10px] font-semibold text-gray-500/90 dark:text-slate-500 w-full leading-tight
                  group-hover:text-gray-600 dark:group-hover:text-slate-400 transition-colors
                `}
                            >
                                { subtext }
                            </div>
                        </div>
                    ) }
                </div>
            </div>
        </Card>
    )
} )
